import 'C:\\Projects\\genkei\\zumeco\\static\\doodle\\view\\Spreadsheet.tsx.esbuild-plugin-windicss.css';
import type jQuery from 'jquery';
import { version as LuckysheetVersion } from 'luckysheet-nightly/package.json';
import { memo } from 'preact/compat';
import { useEffect } from 'preact/hooks';
import type { Writable } from 'ts-essentials';
import type { Luckysheet, LuckysheetFunction, LuckysheetLocale, LuckysheetSheet } from '../../../server/src/luckysheet-models';
import { SpreadsheetSheet } from '../../../server/src/models';
import { debounce, isMobileSafari } from '../../shared/utils';
import './spreadsheet.css';
import { compressSpreadsheetSheet, diffSpreadsheetSheetData, emptySpreadsheetSheet, mergeSpreadsheetSheetData, pushSpreadsheetSheetData } from './spreadsheetData';
declare const $: typeof jQuery;
declare const luckysheet: Luckysheet;
declare const luckysheet_function: Record<string, LuckysheetFunction>;

// 住友重機械ギヤボックス様用の機能でした...
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const spreadsheetEnabled = (_currentUser: {
  readonly email?: string;
} | undefined) =>
// !!(
//   currentUser?.email?.endsWith('@kabuku.co.jp') ||
//   currentUser?.email?.endsWith('@genkei.co.jp') ||
//   currentUser?.email?.endsWith('@shi-g.com')
// )
false;
const loadLuckysheet = (() => {
  let loadedLuckysheet: undefined | Promise<void>;
  return () => loadedLuckysheet ??= (async () => {
    const link = document.head.appendChild(document.createElement('link'));
    link.rel = 'stylesheet';
    link.href = `/luckysheet/luckysheet.css?${LuckysheetVersion}`;
    const [, {
      default: ja
    }] = await Promise.all([new Promise(resolve => {
      const script = document.head.appendChild(document.createElement('script'));
      script.onload = resolve;
      script.src = `/luckysheet/luckysheet-without-locales.iife.js?${LuckysheetVersion}`;
    }), import(`/luckysheet/locales/ja.js?${LuckysheetVersion}`) as Promise<{
      default: Writable<LuckysheetLocale>;
    }>]);
    ja.fontarray.unshift('c6c880e8');
    (ja.formulaMore as Record<string, string>).luckysheet = '';
    luckysheet.locales.ja = ja;

    // ペインサイズ変更追従（resize() 1 回だとスクロールバー分のマージンができたりできなかったりする）
    new ResizeObserver(() => luckysheet.resize()).observe(document.getElementById('cec0f84e')!.parentElement!.parentElement!, {
      box: 'border-box'
    });
  })();
})();
const maxRowColumn = (sheet: LuckysheetSheet) => {
  const data = sheet.data;
  let maxRowIndex = -1;
  let maxColumnIndex = -1;
  for (let rowIndex = 0; rowIndex < data.length; rowIndex++) {
    const row = data[rowIndex];
    for (let columnIndex = 0; columnIndex < row.length; columnIndex++) {
      const cell = row[columnIndex];
      if (cell && (cell.m || cell.ct?.s)) {
        maxRowIndex = rowIndex;
        maxColumnIndex = Math.max(maxColumnIndex, columnIndex);
      }
    }
  }
  return [maxRowIndex, maxColumnIndex];
};
const escapeCsvElement = (s: string | undefined) => s ? s.replace(/"/g, '""') : '';

// todo: 文字コードと改行コードを受け取る
export const spreadsheetToCsv = () => {
  let csv = '';
  const sheet = luckysheet.getSheet();
  const [maxRowIndex, maxColumnIndex] = maxRowColumn(sheet);
  for (let rowIndex = 0; rowIndex <= maxRowIndex; rowIndex++) {
    const row = sheet.data[rowIndex];
    for (let columnIndex = 0; columnIndex <= maxColumnIndex; columnIndex++) {
      const cell = row[columnIndex];
      const value = escapeCsvElement(cell?.m ?? cell?.ct?.s?.map(s => s.v).join(''));
      columnIndex !== 0 && (csv += ',');
      value && (csv += `"${value}"`);
    }
    csv += '\n';
  }
  return csv;
};
export const spreadsheetToImage = () => {
  const images: string[] = [];
  for (const sheet of luckysheet.getAllSheets()) {
    const [maxRowIndex, maxColumnIndex] = maxRowColumn(sheet);
    if (maxRowIndex !== -1 && maxColumnIndex !== -1) {
      images.push(luckysheet.getScreenshot({
        range: {
          row: [0, maxRowIndex],
          column: [0, maxColumnIndex]
        }
      }));
    }
  }
  return images;
};
let initialized = false;
let master = emptySpreadsheetSheet();
export const resetSpreadsheetData = (data: Partial<SpreadsheetSheet>) => {
  master = {
    ...emptySpreadsheetSheet(),
    ...data
  };
  initialized && pushSpreadsheetSheetData(master);
};
export const interpretSpreadsheetCommand = (diff: SpreadsheetSheet) => {
  const oldMaster = master;
  master = mergeSpreadsheetSheetData(master, diff);
  initialized && diffSpreadsheetSheetData(oldMaster, master) && pushSpreadsheetSheetData(master);
};
let onUpdateSpreadsheet: undefined | (() => void);
export const setSpreadsheetUpdateListener = (onUpdate: (diff: SpreadsheetSheet) => void) => {
  onUpdateSpreadsheet = debounce(() => {
    const data = compressSpreadsheetSheet(luckysheet.getSheet());
    const diff = diffSpreadsheetSheetData(master, data);
    master = data;
    diff && onUpdate(diff);
  }, 0);
};
addEventListener('keydown', e => {
  // ショートカットキーの無効化: Undo / Redo
  if ((e.ctrlKey || e.metaKey) && 'zy'.includes(e.key) && e.target instanceof HTMLElement && (e.target.closest('#luckysheet-input-box') || e.target.closest('#cec0f84e'))) {
    e.stopPropagation();
    return;
  }
  // セル編集中に Ctrl + C など押下すると選択が解除されてしまってコピーなどの操作ができない不具合修正
  if ((e.keyCode === 17 /* ctrl */ || e.keyCode === 91 /* win / left menu */ || e.keyCode === 93 /* apps / right menu */ || (e.ctrlKey || e.metaKey) && 'cvx'.includes(e.key)) && e.target instanceof HTMLElement && (e.target.closest('#luckysheet-input-box') as HTMLElement | undefined)?.style?.top) {
    e.stopPropagation();
  }
}, true);
addEventListener('click', event => {
  if ((event.target as Element).closest('#luckysheet-icon-morebtn')) {
    const moreItemsContainer = document.getElementById('luckysheet-icon-morebtn-div');
    const expressionBar = document.getElementById('luckysheet-wa-calculate');
    moreItemsContainer && expressionBar && (moreItemsContainer.style.top = `${expressionBar.getBoundingClientRect().y - 1}px`);
  }
}, true);

// 画像の貼り付けを抑止
// Excel はセルコピーするとクリップボードに画像が含まれる点に注意
addEventListener('paste', event => {
  const clipboardData = event.clipboardData;
  if (clipboardData?.files[0]?.type.includes('image')) {
    const text = clipboardData.getData('text/plain');
    const html = clipboardData.getData('text/html') || text;
    text || event.preventDefault();
    html.includes('table') || event.stopPropagation();
  }
}, true);
const once = (fn: () => void) => {
  let called = false;
  return () => {
    if (!called) {
      called = true;
      fn();
    }
  };
};
const changeFrozenButton = once(() => {
  $('#luckysheet-freezen-btn-horizontal').attr('data-tips', 'ウィンドウ枠の固定').off('click').on('click', function () {
    // 本家が翻訳されたテキストコンテンツで判定してるので同様に...
    // https://github.com/mengshukeji/Luckysheet/blob/v2.1.13/src/controllers/handler.js#L4934
    const locale = luckysheet.locales[luckysheet.store.lang] as unknown as {
      freezen: {
        freezenCancel: string;
      };
    };
    // eslint-disable-next-line @typescript-eslint/no-deprecated
    if ($.trim($(this).text()) === locale.freezen.freezenCancel) {
      luckysheet.cancelFrozen();
      return;
    }
    const range = luckysheet.getRange()[0];
    const row = range?.row[0];
    const column = range?.column[0];
    if (row && column) {
      luckysheet.setBothFrozen(true, {
        range: {
          row_focus: row - 1,
          column_focus: column - 1
        }
      });
    } else if (row) {
      luckysheet.setHorizontalFrozen(true, {
        range: {
          row_focus: row - 1
        }
      });
    } else if (column) {
      luckysheet.setVerticalFrozen(true, {
        range: {
          column_focus: column - 1
        }
      });
    }
  });
});

// ウィンドウ枠の固定状態監視
const observeFrozen = (() => {
  let replaced = false;
  return () => {
    if (replaced) {
      return;
    }
    // eslint-disable-next-line @typescript-eslint/unbound-method
    const original_createAssistCanvas = luckysheet.freezeController.createAssistCanvas;
    luckysheet.freezeController.createAssistCanvas = function () {
      onUpdateSpreadsheet?.();
      // eslint-disable-next-line prefer-rest-params
      return original_createAssistCanvas.apply(this, arguments as IArguments & []);
    };
    replaced = true;
  };
})();
const initSpreadsheet = ({
  editable
}: {
  editable: boolean;
}) => {
  const data = initialized && luckysheet.store.luckysheetfile || [{
    index: '0',
    name: 'Sheet1'
  }];
  initialized = false;
  return new Promise<void>(resolve => {
    luckysheet.create({
      container: 'cec0f84e',
      lang: 'ja',
      data,
      allowEdit: editable,
      showtoolbar: editable,
      enableAddRow: editable,
      enableAddBackTop: false,
      sheetFormulaBar: editable,
      showtoolbarConfig: {
        undoRedo: false,
        // undo / redo の挙動がかなりあやしいので無効にしています。
        currencyFormat: false,
        percentageFormat: false,
        font: false,
        // Web フォントにしないとつらそうなので無効にしています。
        image: false,
        chart: false,
        screenshot: false,
        textRotateMode: false,
        postil: false,
        pivotTable: false,
        dataVerification: false,
        // 入力規則: 保存がめんどくさそうなので無効にしています。
        protection: false,
        // シート保護: たぶんこのアプリには求められないと思います。
        print: false,
        // 印刷: メニューのガワだけ用意されていて中身が未実装のようです。
        splitColumn: false,
        // テキストのセル分割: Excel にないのでほしがる人がいないと思います。
        sortAndFilter: false,
        // ソート: 使ってみたらフリーズしたので無効にしています。
        conditionalFormat: false,
        // 条件付き書式: 保存がめんどくさそうなので無効にしています。
        findAndReplace: false // 検索・置換: よくわからないメニューがたくさん出るのでひとまず無効にしています。
      },
      showinfobar: false,
      showsheetbar: false,
      // 複数シートを許可するなら保存方法とか課金体系とか見直した方が良いと思います。
      showstatisticBar: true,
      showstatisticBarConfig: {
        count: false,
        view: false,
        zoom: true
      },
      cellRightClickConfig: {
        copyAs: false,
        clear: false,
        matrix: false,
        chart: false,
        image: false,
        data: false,
        cellFormat: false,
        sort: false,
        filter: false,
        columnWidth: editable,
        copy: editable,
        deleteCell: editable,
        deleteColumn: editable,
        deleteRow: editable,
        insertColumn: editable,
        insertRow: editable,
        link: editable,
        paste: editable,
        rowHeight: editable
      },
      hook: {
        workbookCreateAfter: () => {
          // Luckysheet 独自関数（3: *IDCARD, *_SPLINES, EVALUATE, 4: DM_TEXT_*, 5: DATA_CN_STOCK_*, REMOTE）削除
          for (const {
            t,
            n
          } of Object.values(luckysheet_function)) {
            // 3 だったり "3" だったりするので...
            if (t == 3 || t == 4 || t == 5) {
              delete luckysheet_function[n];
            }
          }
          luckysheet.store.functionlist = Object.values(luckysheet_function);
          document.getElementById('cec0f84e')?.classList.toggle('spreadsheet-readonly', !editable);
          if (isMobileSafari) {
            // iPad: 選択セルをタップしたとき編集開始します。
            // 本家 Luckysheet は dblclick で編集開始となりますが、 dblclick で focus してもキーボードが表示されません。
            // また、ダブルタップしても :root { touch-action: none } を設定している関係でそもそも dblclick イベントが発火しません。
            // これら実装都合と使い勝手を鑑みて、ずめこでは選択セルのタップで編集開始する挙動としています。
            //
            // 参考:
            //  iOSのMobile Safari上でのfocus()が妙な件を調べてみた - mixi engineer blog
            //  https://mixiengineer.hatenablog.com/entry/2012/10807/
            const cellSelectedElement = document.getElementById('luckysheet-cell-selected')!;
            cellSelectedElement.style.pointerEvents = 'auto';
            cellSelectedElement.addEventListener('click', () => luckysheet.enterEditMode());
          }
          pushSpreadsheetSheetData(master);
          initialized = true;
          resolve();
        },
        updated: () => onUpdateSpreadsheet?.()
      }
    });
  });
};
export const Spreadsheet = memo(({
  editable
}: {
  editable: boolean;
}) => {
  useEffect(() => {
    void loadLuckysheet().then(() => initSpreadsheet({
      editable
    })).then(changeFrozenButton).then(observeFrozen);
  }, [editable]);
  return <div class="relative h-full">
      <div id="cec0f84e" class="absolute inset-0 overflow-hidden" />
    </div>;
});